import { useState } from 'react';
import { ErrorText } from '../../components/ErrorText';
import { FloatingSelectField } from '../../components/FloatingSelectField';
import { InfoNote } from '../../components/InfoNote';
import { SectionHeader } from '../../components/SectionHeader';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { SubSelectCard } from '../../components/SubSelectCard';
import { EmptyTimeslots, Fields, InclusionList, Panel } from './PackageInclusionsPanel.styles';
import type { PackageInclusionsPanelProps } from './PackageInclusionsPanel.types';
import { radius } from '../../theme/tokens';

export function PackageInclusionsPanel({
  inclusions,
  onDayChange,
  onTimeChange,
  labels = {},
}: PackageInclusionsPanelProps) {
  const {
    title = 'Inclusions.',
    subtitle = 'Choose your preferred day and time for each.',
    dayLabel = 'Select day',
    timeLabel = 'Select time',
    noTimeslots = 'No times available',
    noteDismissLabel,
  } = labels;

  const [dismissedNotes, setDismissedNotes] = useState<string[]>([]);

  const dismissNote = (inclusionId: string) =>
    setDismissedNotes((current) => [...current, inclusionId]);

  return (
    <Panel>
      <SectionHeader title={title} subtitle={subtitle} />
      <InclusionList>
        {inclusions.map((inclusion) => (
          <SubSelectCard
            key={inclusion.id}
            heading={inclusion.title}
            subheading={inclusion.subtitle}
          >
            <Fields>
              <FloatingSelectField
                label={dayLabel}
                options={inclusion.dayOptions ?? []}
                value={inclusion.selectedDay}
                onChange={(value) => onDayChange(inclusion.id, value)}
                error={inclusion.dayError}
                fixedValueLabel={inclusion.fixedDayLabel}
                aria-label={`${dayLabel} ${inclusion.title}`}
              />
              {inclusion.isLoading ? (
                <SkeletonBlock height="72px" borderRadius={radius('card', 'radius-md')} />
              ) : inclusion.timeOptions.length ? (
                <FloatingSelectField
                  label={timeLabel}
                  options={inclusion.timeOptions}
                  value={inclusion.selectedTime}
                  onChange={(value) => onTimeChange(inclusion.id, value)}
                  error={inclusion.timeError}
                  aria-label={`${timeLabel} ${inclusion.title}`}
                />
              ) : (
                <EmptyTimeslots
                  data-error={inclusion.timeError ? 'true' : undefined}
                  aria-invalid={inclusion.timeError ? true : undefined}
                  tabIndex={inclusion.timeError ? -1 : undefined}
                >
                  {inclusion.timeError ? <ErrorText>{noTimeslots}</ErrorText> : noTimeslots}
                </EmptyTimeslots>
              )}
            </Fields>
            {inclusion.noteText && !dismissedNotes.includes(inclusion.id) && (
              <InfoNote
                text={inclusion.noteText}
                onDismiss={() => dismissNote(inclusion.id)}
                dismissLabel={noteDismissLabel}
              />
            )}
          </SubSelectCard>
        ))}
      </InclusionList>
    </Panel>
  );
}
